/**
 * Download Service
 * Handles downloading purchased digital products
 */
import apiClient from './apiClient';

export interface DownloadItem {
  id: number;
  productId: number;
  productTitle: string;
  downloadUrl: string;
  purchaseDate: string;
  expiryDate: string;
  isExpired: boolean;
}

/**
 * Get all downloadable items for the current user
 */
export const getUserDownloads = async (): Promise<DownloadItem[]> => {
  try {
    const response = await apiClient.get('/downloads');
    return response.data;
  } catch (error) {
    console.error('Error fetching downloads:', error);
    return [];
  }
};

/**
 * Get download for a specific product
 */
export const getProductDownload = async (productId: number): Promise<DownloadItem | null> => {
  try {
    const response = await apiClient.get(`/downloads/product/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product download:', error);
    return null;
  }
};

/**
 * Generate a download link for a product
 * This will create a temporary, signed URL that allows the user to download the product
 */
export const generateDownloadLink = async (downloadId: number): Promise<string> => {
  try {
    const response = await apiClient.post(`/downloads/${downloadId}/link`);
    return response.data.downloadUrl;
  } catch (error) {
    console.error('Error generating download link:', error);
    throw new Error('Failed to generate download link');
  }
};

/**
 * Download a product file directly
 * This will trigger the browser download
 */
export const downloadProduct = async (downloadId: number): Promise<void> => {
  try {
    const url = await generateDownloadLink(downloadId);

    // Create a temporary anchor element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', ''); // Suggest that this is a download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading product:', error);
    throw new Error('Failed to download product');
  }
};

/**
 * Legacy interface for file metadata
 */
export interface FileMetadata {
  name: string;
  size: string;
  type: string;
  url?: string; // Download URL for the file
}

/**
 * File info returned from the API
 */
interface ApiFileInfo {
  fileName: string;
  fileSize: string;
  fileType: string;
  temporaryUrl: string;
}

/**
 * Get downloadable files for a specific order (legacy support)
 * Maps to the new API-based system
 */
export const getOrderFiles = async (orderId: string): Promise<FileMetadata[]> => {
  try {
    const response = await apiClient.get(`/downloads/order/${orderId}`);
    // Transform API response to match old interface
    return response.data.map((item: ApiFileInfo) => ({
      name: item.fileName,
      size: item.fileSize,
      type: item.fileType,
      url: item.temporaryUrl
    }));
  } catch (error) {
    console.error('Error fetching order files:', error);
    return [];
  }
};

/**
 * Generate a temporary download URL for a file (legacy support)
 */
export const getDownloadUrl = async (fileName: string, orderId: string): Promise<string> => {
  try {
    const response = await apiClient.get(`/downloads/order/${orderId}/file/${encodeURIComponent(fileName)}`);
    return response.data.downloadUrl;
  } catch (error) {
    console.error('Error getting download URL:', error);
    throw new Error('Failed to get download URL');
  }
};

/**
 * Trigger a download for a file (legacy support)
 */
export const downloadFile = (fileName: string, url: string): void => {
  // Create a temporary anchor element
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
